import * as React from "react";
import { BaseBlock, BaseBlockOptions, DeviceDetect, broadcastBlockLoad, SportDataGateway } from "sb-resp-lib";

import { BaseDict } from "./dict";
import { config } from "./config";
import { DataFacade } from "../../DataFacade";
import { DataParser, MoneyLineAllMarketFilter, MarketFilterController } from "../../DataParser";
import { PersonalizedHighlightsData } from "./data";
import { PersonalizedHighlights } from "./PersonalizedHighlights";
import { SportDataGateWayRetriever } from "../../DataFacade/SportDataGateWayRetriever";

import * as MOCKSTYPE from "sb-resp-lib/lib/mocks";
import * as LOCALMOCKS from "../../Mocks";
import { DateTimeFormatter } from "../../Helpers";

let _localMocks: typeof LOCALMOCKS;
let _libMocks: typeof MOCKSTYPE;
let PersonalizationMockedMethods;
let SportDataGateWayRetrieverMock;
let CmsLocalMock;
let UserInfoMock;
let LangCodeIdMock;
let CurrentOddStyleMock;
let NavHandlerMock;
let BetSlipUtilMock;
let TimeControlMock;
let GetUniqueOddsClassMock;
let deviceChangeDispatcherMock;
let statisticsProvider;

if (process.env.NODE_ENV === "development") {

    _libMocks = require("sb-resp-lib/lib/mocks");
    deviceChangeDispatcherMock = _libMocks.DeviceChangeDispatcherMock.getInstance()
    // Here we use our local mocks in development mode
    _localMocks = require("../../Mocks");
    PersonalizationMockedMethods = _localMocks.PersonalizationMockedMethods;
    SportDataGateWayRetrieverMock = _localMocks.SportDataGateWayRetrieverMock;
    UserInfoMock = _libMocks.UserInfoMock;
    LangCodeIdMock = _localMocks.LocaleCodeMock;
    CurrentOddStyleMock = _localMocks.CurrentOddStyleCodeMock;
    CmsLocalMock = _localMocks.CmsMock;
    NavHandlerMock = _libMocks.NavHandlerMock;
    BetSlipUtilMock = _libMocks.BetSlipUtilMock;
    TimeControlMock = _libMocks.TimeControlMock;
    GetUniqueOddsClassMock = _libMocks.getUniqueOddsClass;
    statisticsProvider = _localMocks.StatisticsContentProviderMock;
}

export class PersonalizedHighlightsRoot extends BaseBlock {
    dataProvider!: PersonalizedHighlightsData;
    options!: BaseBlockOptions;
    dataFacade!: DataFacade;

    // constructor sets defaults
    constructor(
        domId: string,
        options: BaseBlockOptions = {
            dict: BaseDict,
            cms: {},
            navHandler: {},
            dataProvider: {},
            betSlipUtil: {},
            gameStatus: {},
            timeControl: {},
            userInfo: {},
            panelId: ""
        }
    ) {
        super(domId, options);

        if (process.env.NODE_ENV === "development") {

            // setup dictionary for debug build
            this.options.dict = BaseDict;

            let cmsMock = new CmsLocalMock();

            this.dataFacade = new DataFacade({
                pageMethods: new PersonalizationMockedMethods(),
                sportDataGatewayRetriever: new SportDataGateWayRetrieverMock(LangCodeIdMock, new SportDataGateway()),
                statisticsProvider: new statisticsProvider(true, 1),
                utils: {
                    betSlipUtil: new BetSlipUtilMock(),
                },
                navHandler: new NavHandlerMock(),
                timeControl: new TimeControlMock(new Date()),
                currentOddStyleIndex: CurrentOddStyleMock,
                userInfo: UserInfoMock,
                cms: cmsMock,
                deviceChangeDispatcher: deviceChangeDispatcherMock,
                dict: this.options.dict,
                blockUniqueId: domId
            });

            let dateTimeFormatterDic = {
                today: this.options.dict.Today,
                tomorrow: this.options.dict.Tomorrow,
                months: this.options.dict.Months
            }

            let dateTimeConfig = {
                enableLongDateNamesIsMobile: false,
                enableLongDateNamesIsTablet: false,
                enableLongDateNamesIsDesktop: true
            }

            let dateTimeFormatter = new DateTimeFormatter(this.dataFacade.timeControl, dateTimeFormatterDic, dateTimeConfig);
            let highlightsDataParser = new DataParser({
                dataFacade: this.dataFacade,
                dateTimeFormatter,
                deviceDetect: DeviceDetect.getInstance(),
                marketFilterController: new MarketFilterController(undefined, new MoneyLineAllMarketFilter()),
            });
            this.dataProvider = new PersonalizedHighlightsData(highlightsDataParser, GetUniqueOddsClassMock);

            //export data store to window for debug purposes
            window["PersonalizedHighlightsDATA"] = this.dataProvider;
        }

        if (process.env.NODE_ENV === "production" && typeof options.dataProvider !== "undefined") {

            // if no provided dictionary is not full fill in the missing keys with the base one
            this.options.dict = { ...BaseDict, ...(options.dict || {}) };

            // merge default and CMS options
            this.options.cms = { ...config, ...(options.cms || {}) };

            this.dataFacade = new DataFacade({
                pageMethods: options.dataProvider.pageMethods,
                sportDataGatewayRetriever: new SportDataGateWayRetriever(
                    options.dataProvider.languageCode, new SportDataGateway()),
                statisticsProvider: this.options.dataProvider.statisticsProvider,
                utils: {
                    betSlipUtil: options.betSlipUtil
                },
                navHandler: options.navHandler,
                currentOddStyleIndex: options.dataProvider.oddStyleCode,
                timeControl: options.timeControl,
                cms: options.cms,
                dict: this.options.dict,
                userInfo: options.userInfo,
                blockUniqueId: domId
            });

            let dateTimeFormatterDic = {
                today: this.options.dict.Today,
                tomorrow: this.options.dict.Tomorrow,
                months: this.options.dict.Months
            }
            let dateTimeConfig = {
                enableLongDateNamesIsMobile: false,
                enableLongDateNamesIsTablet: false,
                enableLongDateNamesIsDesktop: this.options.dataProvider.useMonthName
            }
            let dateTimeFormatter = new DateTimeFormatter(options.timeControl, dateTimeFormatterDic, dateTimeConfig);
            let highlightsDataParser = new DataParser({
                dataFacade: this.dataFacade,
                dateTimeFormatter,
                deviceDetect: DeviceDetect.getInstance(),
                marketFilterController: new MarketFilterController(undefined, new MoneyLineAllMarketFilter()),
            });
            this.dataProvider = new PersonalizedHighlightsData(highlightsDataParser, options.dataProvider.getUniqueOddsClass);
        }
    }

    activate() {
        return super.activate(
            <PersonalizedHighlights dataProvider={this.dataProvider} dict={this.options.dict} />
        );
    }

    getTabLabel() {
        return this.options.dict.PersonalizedHighlightsTabLabel;
    }

    deactivate() {
        return super.deactivate();
    }
}

window["PersonalizedHighlights"] = PersonalizedHighlightsRoot;
broadcastBlockLoad("PersonalizedHighlights");